'use client';

import * as React from 'react';

import { Button } from '@/hen-ui/atomics/Button';
import { Icon } from '@/hen-ui/atomics/Icon';
import { Text } from '@/hen-ui/atomics/Text';
import { Flex } from '@/hen-ui/composites/Flex';
import { cn } from '@/hen-ui/utils';

import { ChartErrorBoundary } from './ChartErrorBoundary';
import type { ChartSurfaceState } from './chart-surface.types';

const DEFAULT_QUERY_ERROR = 'An unexpected error occurred.';

/**
 * Stale-banner copy: either a fully custom `staleMessage` **or** an optional
 * `staleTimeLabel` for the default template — never both (enforced at compile time).
 */
export type ChartFrameStaleBannerProps =
  | { staleMessage: React.ReactNode; staleTimeLabel?: never }
  | { staleMessage?: never; staleTimeLabel?: string };

export interface ChartFramePropsBase
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title' | 'children'> {
  children: React.ReactNode;
  /**
   * Which surface to show — derived by the caller from query state (loading → error → empty → ready).
   */
  surface: ChartSurfaceState;
  /**
   * User-facing detail for the query error panel when `surface === 'queryError'`.
   * When omitted or blank after trim, a generic fallback message is shown.
   */
  queryErrorMessage?: string;
  /**
   * When true, shows escalation copy after repeated failures (caller decides when, e.g. after N retries).
   */
  showContactSupport?: boolean;
  /** Data is older than the agreed freshness window — warning banner above chart. */
  isStale?: boolean;
  /** Re-run the query (query error Retry). */
  onRetry?: () => void;
  /** Optional title shown above the chart area on every surface. */
  title?: React.ReactNode;
  /** Optional subtitle below the title. */
  subtitle?: React.ReactNode;
  /** Empty state title. */
  emptyTitle?: React.ReactNode;
  /** Empty state description. */
  emptyDescription?: React.ReactNode;
  /** Optional secondary action (e.g. reset filters) on empty state. */
  onResetFilters?: () => void;
  resetFiltersLabel?: string;
  /**
   * When this value changes (e.g. new `queryId`), a dismissed stale banner shows again.
   */
  staleBannerKey?: string | number;
  /**
   * Tailwind class for the body's minimum height when `fillContainer=false`
   * (content-size mode). Ignored in fill mode — the chart child carries its
   * own `minHeight` (its `height` prop) which propagates upward through the
   * `flex-1` chain. Defaults to `min-h-60`.
   */
  minHeightClassName?: string;
  /**
   * When true, the frame's body and ready-surface wrappers use `flex-1` (with
   * `min-height: auto`) so the chart child can:
   * 1. Contribute its own `height` (treated as `min-height` by the chart composite)
   *    upward into the layout's auto-sizing chain — the same bottom-up sizing path
   *    fixed-pixel charts use.
   * 2. Stretch via `h-full` to fill any extra height the row provides.
   *
   * Pair with a chart that has `fillContainer={true}`. The consumer is responsible
   * for giving the ChartFrame card a height-claiming className (typically `flex-1`
   * inside a `flex flex-col` grid cell, or `h-full` directly) — this prop only
   * wires the frame's internal flex chain so the chart can grow within the card.
   */
  fillContainer?: boolean;
  /** Extra class names for the loading skeleton block. */
  skeletonClassName?: string;
  /** Forward render-time chart errors to logging or APM (distinct from query `onRetry`). */
  onChartRenderError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

/** @see {@link ChartFrameStaleBannerProps} — `staleMessage` and `staleTimeLabel` are mutually exclusive. */
export type ChartFrameProps = ChartFramePropsBase & ChartFrameStaleBannerProps;

const DEFAULT_EMPTY_TITLE = 'No data matches your filters.';

function resolveQueryErrorMessage(queryErrorMessage?: string): string {
  if (queryErrorMessage == null) return DEFAULT_QUERY_ERROR;
  const trimmed = queryErrorMessage.trim();
  return trimmed !== '' ? trimmed : DEFAULT_QUERY_ERROR;
}

/**
 * Shared shell for dashboard charts: loading skeleton, query error + retry, empty state,
 * stale-data banner, and a render error boundary around chart children.
 *
 * **Surface:** The caller owns precedence (e.g. loading → error → empty → ready) and passes
 * `surface` accordingly. Stale banner appears only when the chart surface is `ready` and `isStale` is true.
 *
 * **Stale copy:** Pass either `staleMessage` (fully custom) or optionally `staleTimeLabel` (default template
 * fragment), not both — enforced by `ChartFrameProps` / {@link ChartFrameStaleBannerProps}.
 */
export const ChartFrame = React.forwardRef<HTMLDivElement, ChartFrameProps>(
  (
    {
      children,
      surface,
      queryErrorMessage,
      showContactSupport = false,
      isStale = false,
      onRetry,
      title,
      subtitle,
      emptyTitle = DEFAULT_EMPTY_TITLE,
      emptyDescription,
      onResetFilters,
      resetFiltersLabel = 'Reset filters',
      staleMessage,
      staleTimeLabel,
      staleBannerKey,
      minHeightClassName = 'min-h-60',
      fillContainer = false,
      skeletonClassName,
      onChartRenderError,
      className,
      ...rest
    },
    ref,
  ) => {
    const [boundaryRemountKey, setBoundaryRemountKey] = React.useState(0);
    const [staleDismissed, setStaleDismissed] = React.useState(false);

    React.useEffect(() => {
      setStaleDismissed(false);
    }, [staleBannerKey]);

    const handleBoundaryRemount = () => {
      setBoundaryRemountKey((k) => k + 1);
    };

    const showStaleBanner = surface === 'ready' && isStale && !staleDismissed;

    const defaultStaleCopy = staleTimeLabel
      ? `Data last updated ${staleTimeLabel}. Expected daily refresh may be delayed.`
      : 'Data may be older than expected. Expected daily refresh may be delayed.';

    return (
      <div
        ref={ref}
        className={cn(
          'flex w-full flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white',
          // The consumer claims height via className (e.g. `flex-1` inside a
          // grid cell that's `flex flex-col`, or `h-full`). This prop only
          // wires the internal flex chain so the chart can grow within.
          className,
        )}
        {...rest}
      >
        {showStaleBanner && (
          <div
            className='flex flex-row items-start gap-sm border-b border-warning-500 bg-warning-100 p-b1'
            role='status'
          >
            <Icon
              icon='alert-triangle'
              size='sm'
              color='warning'
              className='shrink-0 self-start'
              aria-hidden
            />
            <Text variant='l1' className='min-w-0 flex-1 text-warning-900'>
              {staleMessage ?? defaultStaleCopy}
            </Text>
            <Button
              type='button'
              variant='textOnly'
              size='flush'
              className='shrink-0 text-warning-900'
              onClick={() => setStaleDismissed(true)}
              aria-label='Dismiss stale data warning'
            >
              <Icon icon='x' size='sm' color='warning' aria-hidden />
            </Button>
          </div>
        )}

        <div
          className={cn(
            'flex flex-col gap-sm p-d3',
            // In fill mode: flex-1 grows into the card. We omit `min-h-0` so
            // the default `min-height: auto` lets the chart child's min-height
            // propagate upward (bottom-up sizing). With min-h-0 the chart's
            // 300px floor would be invisible to the parent grid row.
            fillContainer ? 'flex-1' : minHeightClassName,
          )}
        >
          {(title != null || subtitle != null) && (
            <div className='flex flex-col gap-xxs'>
              {title != null ? (
                <Text
                  variant='b1'
                  weight='semibold'
                  className='text-neutral-900'
                >
                  {title}
                </Text>
              ) : null}
              {subtitle != null ? (
                <Text variant='l1' className='text-neutral-600'>
                  {subtitle}
                </Text>
              ) : null}
            </div>
          )}

          {surface === 'loading' && (
            <ChartSkeleton className={skeletonClassName} />
          )}

          {surface === 'queryError' && (
            <QueryErrorPanel
              message={resolveQueryErrorMessage(queryErrorMessage)}
              onRetry={() => onRetry?.()}
              retryDisabled={!onRetry}
              showContactSupport={showContactSupport}
            />
          )}

          {surface === 'empty' && (
            <Flex
              direction='column'
              align='center'
              justify='center'
              gap='md'
              className='flex-1 py-d2'
            >
              <Icon icon='inbox' size='xl' color='neutral' aria-hidden />
              <Text variant='b2' align='center' weight='semibold'>
                {emptyTitle}
              </Text>
              {emptyDescription ? (
                <Text
                  variant='l1'
                  align='center'
                  className='max-w-md text-neutral-600'
                >
                  {emptyDescription}
                </Text>
              ) : null}
              {onResetFilters ? (
                <Button
                  type='button'
                  variant='primaryOutline'
                  onClick={onResetFilters}
                >
                  {resetFiltersLabel}
                </Button>
              ) : null}
            </Flex>
          )}

          {surface === 'ready' &&
            (fillContainer ? (
              /* flex-1 lets this wrapper grow into the body; the chart child
                   (with `fillContainer={true}`) carries its own `minHeight:
                   <height>` style which propagates up through the flex chain —
                   same bottom-up sizing path as fixed-pixel charts. */
              <div className='flex flex-1 flex-col'>
                <ChartErrorBoundary
                  remountKey={boundaryRemountKey}
                  onRemountRequest={handleBoundaryRemount}
                  onError={onChartRenderError}
                >
                  {children}
                </ChartErrorBoundary>
              </div>
            ) : (
              <ChartErrorBoundary
                remountKey={boundaryRemountKey}
                onRemountRequest={handleBoundaryRemount}
                onError={onChartRenderError}
              >
                {children}
              </ChartErrorBoundary>
            ))}
        </div>
      </div>
    );
  },
);

ChartFrame.displayName = 'ChartFrame';

function ChartSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'flex flex-1 flex-col justify-center gap-md py-sm',
        className,
      )}
      aria-busy
      aria-label='Loading chart'
    >
      <div className='h-4 w-2/3 animate-pulse rounded-md bg-neutral-200' />
      <div className='h-44 w-full animate-pulse rounded-md bg-neutral-100' />
      <div className='flex gap-sm'>
        <div className='h-3 w-24 animate-pulse rounded bg-neutral-200' />
        <div className='h-3 w-24 animate-pulse rounded bg-neutral-200' />
        <div className='h-3 w-24 animate-pulse rounded bg-neutral-200' />
      </div>
    </div>
  );
}

function QueryErrorPanel({
  message,
  onRetry,
  retryDisabled,
  showContactSupport,
}: {
  message: string;
  onRetry: () => void;
  retryDisabled: boolean;
  showContactSupport: boolean;
}) {
  return (
    <Flex
      direction='column'
      align='center'
      justify='center'
      gap='md'
      className='flex-1 py-d2'
      role='alert'
    >
      <Icon icon='circle-alert' size='lg' color='error' aria-hidden />
      <Text
        variant='b2'
        align='center'
        weight='semibold'
        className='text-neutral-900'
      >
        Unable to load data.
      </Text>
      <Text variant='l1' align='center' className='max-w-md text-neutral-600'>
        {message}
      </Text>
      {!retryDisabled ? (
        <Button type='button' variant='primaryFill' onClick={onRetry}>
          Try again
        </Button>
      ) : null}
      {showContactSupport ? (
        <Text variant='l2' align='center' className='text-neutral-500'>
          If this continues, contact support.
        </Text>
      ) : null}
    </Flex>
  );
}
